import type { SubQuestion } from '../../../types/helios/Question';
import { isEditableOptionType } from './subQuestionResponseType';
import { countNonEmptyOptions, MIN_OPTIONS } from './optionsValidation';
import { isHtmlContentEmpty } from 'src/validations/translationFormSchema';

/**
 * Pre-publish validation for a question's sub-questions on the AuditWhiz
 * Review flow. The review flow edits live (no per-sub Save button), so these
 * rules can't be enforced at a commit moment the way the manual (v1) flow did
 * at its side-panel Save — instead the publish action gates on them, the row
 * headers flag violating subs, and this module is the single source of truth
 * both read from.
 *
 * House style mirrors {@link auditClosureValidation}: a pure function
 * returning an ARRAY of typed violation records, plus a per-row predicate.
 * Pure — no react, no side effects. Extensible: add a new `kind` + rule as
 * more blockers appear (empty title, etc.).
 */

/** Discriminated reasons a sub-question isn't ready to publish. Each is a
 *  faithful port of a V1 (manual flow) `SubQuestionSidePanel.isFormValid`
 *  rule — see tasks/audit-whiz/validations.md. */
export const SubQuestionViolationKind = {
  /** The sub-question title is blank (whitespace-only counts as blank). */
  EMPTY_TITLE: 'EMPTY_TITLE',
  /** The sub-question instruction is blank. */
  EMPTY_INSTRUCTION: 'EMPTY_INSTRUCTION',
  /** A SINGLE_SELECT / MULTI_SELECT sub has fewer than {@link MIN_OPTIONS}
   *  non-empty answer options. */
  MIN_OPTIONS: 'MIN_OPTIONS',
  /** The sub has an attached task whose required fields aren't complete:
   *  a blank task name or description, or a non-positive duration. Only
   *  applies when a task is present (task itself is optional). */
  TASK_INCOMPLETE: 'TASK_INCOMPLETE',
} as const;

export type SubQuestionViolationKind =
  (typeof SubQuestionViolationKind)[keyof typeof SubQuestionViolationKind];

/** One violation on one sub-question. `index` is 1-based to match the row
 *  header's visible "N." numbering (array position, not `orderIndex`). */
export interface SubQuestionViolation {
  readonly index: number;
  readonly title: string;
  readonly kind: SubQuestionViolationKind;
}

/** Whether a present task is missing a required field. Mirrors V1's `hasTask`
 *  gate: a task is OPTIONAL, but once attached its name + description are
 *  required and its duration (if the field is used) must be positive. Returns
 *  false when there's no task at all. */
function hasIncompleteTask(sub: SubQuestion): boolean {
  const task = sub.taskDetails;
  if (!task) return false;
  return (
    task.taskName.trim().length === 0 ||
    // taskDescription is a rich-text (HTML) field, so a cleared editor stores
    // "<p></p>". Reuse the canonical V1 emptiness check — which uses
    // allowImageOnly here because the manual flow lets auditors submit a
    // description that is ONLY a pasted screenshot (see taskDraftFormSchema).
    isHtmlContentEmpty(task.taskDescription, { allowImageOnly: true }) ||
    (task.taskDurationDays ?? 0) <= 0
  );
}

/** The ordered rule set applied to each sub-question. Each entry pairs a
 *  predicate ("is this rule VIOLATED?") with the kind to emit. Declared once so
 *  the per-sub loop stays flat and new rules are added in one place. */
const SUB_QUESTION_RULES: readonly {
  readonly kind: SubQuestionViolationKind;
  readonly isViolated: (sub: SubQuestion) => boolean;
}[] = [
  {
    kind: SubQuestionViolationKind.EMPTY_TITLE,
    isViolated: (sub) => sub.title.trim().length === 0,
  },
  {
    // The instruction is a rich-text (HTML) field, so a visually-cleared
    // editor still stores markup like "<p></p>". Reuse the canonical V1
    // emptiness check (strict — NO allowImageOnly, matching the manual flow:
    // an instruction must have text, an image-only instruction is not valid).
    kind: SubQuestionViolationKind.EMPTY_INSTRUCTION,
    isViolated: (sub) => isHtmlContentEmpty(sub.instruction),
  },
  {
    kind: SubQuestionViolationKind.MIN_OPTIONS,
    isViolated: (sub) =>
      isEditableOptionType(sub.responseType) &&
      countNonEmptyOptions(sub.options ?? []) < MIN_OPTIONS,
  },
  {
    kind: SubQuestionViolationKind.TASK_INCOMPLETE,
    isViolated: hasIncompleteTask,
  },
];

/**
 * Returns every publish-blocking violation across a question's sub-questions,
 * in row order (a single sub can contribute MULTIPLE violations — e.g. an
 * empty title AND too few options). Empty array === ready to publish.
 *
 * Rules (faithful ports of V1 `SubQuestionSidePanel.isFormValid`, see
 * tasks/audit-whiz/validations.md): title required, instruction required
 * (rich-text — checked via the canonical isHtmlContentEmpty, strict/no
 * image-only), editable select types (SINGLE_SELECT / MULTI_SELECT, NOT
 * YES_NO) need at
 * least {@link MIN_OPTIONS} non-empty options, and an attached task must have
 * name + description + positive duration. Reuses {@link countNonEmptyOptions}
 * so the gate, the inline editor hint, and the row flag all measure
 * identically.
 */
export function validateSubQuestions(
  subs: readonly SubQuestion[],
): SubQuestionViolation[] {
  const violations: SubQuestionViolation[] = [];
  subs.forEach((sub, i) => {
    for (const rule of SUB_QUESTION_RULES) {
      if (rule.isViolated(sub)) {
        violations.push({ index: i + 1, title: sub.title, kind: rule.kind });
      }
    }
  });
  return violations;
}

/**
 * Whether the sub-question at 1-based `index` has any violation — the row
 * header's "incomplete" indicator reads this. Kept as its own predicate
 * (rather than the row re-deriving) so the row and the gate agree.
 */
export function subQuestionHasViolation(
  violations: readonly SubQuestionViolation[],
  index: number,
): boolean {
  return violations.some((v) => v.index === index);
}
